import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import QRCode from "react-qr-code";
import { apiErrorCode } from "@/api/client";
import * as endpoints from "@/api/endpoints";
import { Button, Card, Input, PageTitle } from "@/components/ui/primitives";
import { DataState } from "@/components/data-state";
import { useI18n } from "@/i18n";
import type { ProfileCard } from "@/types";

/**
 * The card the signed-in person hands out at a fair.
 *
 * Two things set this page apart from every other one in the panel:
 *
 *  * **It is not about an organisation.** The card belongs to the account and
 *    goes wherever that account is a member, so its query key carries no
 *    `activeOrgId` — keying it would throw the card away every time somebody
 *    switched organisation, for no reason.
 *  * **It is not plan-gated and shows no plan notice.** A free organisation has
 *    nothing on the server, which is why the record pages have to explain
 *    themselves — but a card is account data and is there on every plan. Making
 *    people pay to hand out their own details would be charging for the thing
 *    that sells the app.
 *
 * The panel does not create records, and this is not an exception to that: the
 * card is account administration, like members and invitations. Contacts other
 * people share still land in the mobile app, where somebody confirms them.
 */

const emptyForm = {
  fullName: "",
  companyName: "",
  title: "",
  phone: "",
  email: "",
  website: "",
  city: "",
  country: "",
};

type Form = typeof emptyForm;

/** Matches the server's caps, which exist so the code stays scannable. */
const limits: Record<keyof Form, number> = {
  fullName: 60,
  companyName: 60,
  title: 40,
  phone: 24,
  email: 60,
  website: 48,
  city: 30,
  country: 30,
};

export function ProfileCardPage() {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);

  const [form, setForm] = useState<Form>(emptyForm);
  const [photoKey, setPhotoKey] = useState<string | undefined>();
  const [notice, setNotice] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const card = useQuery({
    // No `activeOrgId`: see the note above. The card is the same whichever
    // organisation is selected.
    queryKey: ["my-card"],
    queryFn: endpoints.getMyCard,
  });

  useEffect(() => {
    const value = card.data;
    if (!value) return;
    setForm({
      fullName: value.fullName,
      // Shown even when the server filled it in from the name, so nobody has to
      // guess what their code actually gives out.
      companyName: value.companyName,
      title: value.title,
      phone: value.phone,
      email: value.email,
      website: value.website,
      city: value.city,
      country: value.country,
    });
  }, [card.data]);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["my-card"] });

  const saveMutation = useMutation({
    mutationFn: () => endpoints.saveMyCard({ ...form, photoKey }),
    onSuccess: () => {
      setPhotoKey(undefined);
      setNotice(t("cardSaved"));
      void refresh();
    },
    onError: (error: unknown) => {
      const code = apiErrorCode(error);
      setNotice(
        code === "CARD_NO_CONTACT"
          ? t("cardErrorNoContact")
          : code === "CARD_TOO_LARGE"
            ? t("cardErrorTooLarge")
            : t("error")
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: endpoints.deleteMyCard,
    onSuccess: () => {
      setForm(emptyForm);
      setNotice(null);
      void refresh();
    },
  });

  async function onPickPhoto(file: File) {
    setUploading(true);
    setNotice(null);
    try {
      const upload = await endpoints.cardPhotoUploadUrl(file.type);
      await endpoints.uploadCardPhoto(upload.url, file);
      // Held until the card is saved: the key means nothing until it is
      // written onto the card, and an upload the person then abandons should
      // leave their existing photo alone.
      setPhotoKey(upload.key);
      setNotice(t("cardPhotoReady"));
    } catch {
      setNotice(t("error"));
    } finally {
      setUploading(false);
    }
  }

  const field = (key: keyof Form) => ({
    value: form[key],
    maxLength: limits[key],
    onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
      setForm((current) => ({ ...current, [key]: event.target.value })),
  });

  return (
    <div className="space-y-6">
      <PageTitle>{t("cardTitle")}</PageTitle>

      <DataState
        isLoading={card.isLoading}
        isError={card.isError}
        // Never "empty": having no card yet is the normal starting point, and
        // the form is exactly what somebody in that state needs to see.
        isEmpty={false}
        onRetry={() => void card.refetch()}
      >
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
          <Card className="space-y-4 p-4">
            <p className="text-sm text-muted-foreground">{t("cardIntro")}</p>

            <div className="grid gap-3 sm:grid-cols-2">
              <Labelled label={t("cardFullName")}>
                <Input {...field("fullName")} />
              </Labelled>
              <Labelled label={t("cardJobTitle")}>
                <Input {...field("title")} />
              </Labelled>
            </div>

            <Labelled label={t("cardCompanyName")} hint={t("cardCompanyNameHelp")}>
              <Input {...field("companyName")} />
            </Labelled>

            <div className="grid gap-3 sm:grid-cols-2">
              <Labelled label={t("cardPhone")}>
                <Input {...field("phone")} type="tel" />
              </Labelled>
              <Labelled label={t("cardEmail")} hint={t("cardEmailHelp")}>
                <Input {...field("email")} type="email" />
              </Labelled>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <Labelled label={t("cardWebsite")}>
                <Input {...field("website")} />
              </Labelled>
              <Labelled label={t("cardCity")}>
                <Input {...field("city")} />
              </Labelled>
              <Labelled label={t("cardCountry")}>
                <Input {...field("country")} />
              </Labelled>
            </div>

            <div className="flex flex-wrap items-center gap-2 pt-1">
              <Button
                disabled={!form.fullName.trim() || saveMutation.isPending}
                onClick={() => saveMutation.mutate()}
              >
                {t("save")}
              </Button>
              <input
                ref={fileInput}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) void onPickPhoto(file);
                  event.target.value = "";
                }}
              />
              <Button
                variant="secondary"
                disabled={uploading}
                onClick={() => fileInput.current?.click()}
              >
                {uploading ? t("cardPhotoUploading") : t("cardPhoto")}
              </Button>
              {card.data && (
                <Button
                  variant="ghost"
                  disabled={deleteMutation.isPending}
                  onClick={() => {
                    if (window.confirm(t("cardStopSharingWarning"))) {
                      deleteMutation.mutate();
                    }
                  }}
                >
                  {t("cardStopSharing")}
                </Button>
              )}
            </div>

            {notice && <p className="text-sm text-muted-foreground">{notice}</p>}
          </Card>

          <QrPanel card={card.data ?? null} />
        </div>
      </DataState>
    </div>
  );
}

function Labelled({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block space-y-1">
      <span className="text-sm font-medium">{label}</span>
      {children}
      {hint && <span className="block text-xs text-muted-foreground">{hint}</span>}
    </label>
  );
}

/**
 * The code somebody else points a camera at.
 *
 * Worth having on a laptop as well as a phone: it prints, so it can go on a
 * stand sign where everybody walking past can scan it without anyone holding
 * anything up.
 *
 * Always drawn black on white whatever the panel's theme. An inverted code is
 * unreadable to a good half of the scanners out there, and there is no way to
 * find that out from this side.
 */
function QrPanel({ card }: { card: ProfileCard | null }) {
  const { t } = useI18n();

  if (!card) {
    return (
      <Card className="space-y-2 p-4">
        <p className="text-sm font-medium">{t("cardNotSetUp")}</p>
        <p className="text-sm text-muted-foreground">{t("cardNotSetUpHint")}</p>
      </Card>
    );
  }

  return (
    <Card className="space-y-3 p-4 text-center">
      <div className="mx-auto w-fit rounded-lg bg-white p-4">
        <QRCode value={card.qrUrl} size={220} level="M" />
      </div>
      {card.photoUrl && (
        <img
          src={card.photoUrl}
          alt=""
          className="mx-auto h-16 w-16 rounded-full object-cover"
        />
      )}
      <div>
        <p className="font-medium">{card.fullName}</p>
        {card.kind === "company" && card.companyName && (
          <p className="text-sm">{card.companyName}</p>
        )}
        {card.title && <p className="text-sm text-muted-foreground">{card.title}</p>}
      </div>
      <p className="text-xs text-muted-foreground">{t("cardShowCodeHint")}</p>
      <Button variant="secondary" onClick={() => window.print()}>
        {t("cardPrint")}
      </Button>
    </Card>
  );
}
